import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { bazaarItemUrl } from '../lib/slugId';
import { PageHeader } from '../components/PageHeader';
import { formatPrice } from '../lib/price';
import type { BazaarItem, Paginated } from '../api/types';

function sourceFromUrl(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

export function Bazaar() {
  const { t } = useTranslation();
  const [items, setItems] = useState<BazaarItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch<Paginated<BazaarItem>>('/api/v1/bazaar-items?sort=-created_at&per_page=24')
      .then((res) => setItems(res.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
      <PageHeader />
      <Stack spacing={3}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Typography variant="h4">{t('bazaar.title')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          component={RouterLink}
          to="/bazaar/new"
        >
          {t('common.new')}
        </Button>
      </Stack>

      {items.length === 0 ? (
        <Typography color="text.secondary">{t('bazaar.noItems')}</Typography>
      ) : (
        <Grid container spacing={3}>
          {items.map((item) => {
            const price = formatPrice(item.price_cents, item.currency);
            const firstPhoto = item.photo_urls[0];
            return (
              <Grid key={item.id} size={{ xs: 12, sm: 6, md: 4 }}>
                <Card>
                  <CardActionArea component={RouterLink} to={bazaarItemUrl(item)}>
                    {firstPhoto && (
                      <CardMedia
                        component="img"
                        height="180"
                        image={firstPhoto}
                        alt={item.title}
                      />
                    )}
                    <CardContent>
                      <Typography variant="h6">{item.title}</Typography>
                      {price && (
                        <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                          {price}
                        </Typography>
                      )}
                      {item.description && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {item.description}
                        </Typography>
                      )}
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: 1 }}
                      >
                        {sourceFromUrl(item.external_url)}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
      </Stack>
    </Container>
  );
}
